import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useHistoricoDocumentosAssinadosQuery } from '@/hooks/useAssinaturas'
import { ExternalLink, FileText, Loader2 } from 'lucide-react'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

function formatarData(s: string | null | undefined): string {
  if (!s) return '—'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return String(s)
  }
}

const TIPO_LABEL: Record<string, string> = {
  triagem: 'Triagem',
  consulta_oftalmologica: 'Consulta Oftalmológica',
  prontuario_geralista: 'Prontuário Geralista',
  prontuario_retinologo: 'Prontuário Retinólogo',
  avaliacao_cardiologica: 'Avaliação Cardiológica',
  anamnese_pre_operatoria: 'Anamnese Pré-Operatória',
  encaminhamento_cirurgico: 'Encaminhamento Cirúrgico'
}

interface HistoricoDocumentosAssinadosModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  prontuarioId: number
  tenantRef: string | number
}

export function HistoricoDocumentosAssinadosModal({
  open,
  onOpenChange,
  prontuarioId,
  tenantRef
}: HistoricoDocumentosAssinadosModalProps) {
  const { data: itens = [], isLoading } = useHistoricoDocumentosAssinadosQuery(
    open ? prontuarioId : undefined
  )

  const urlValidar = (codigo: string) =>
    `${BASE_URL}/public/validar-documento/${tenantRef}/${codigo}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documentos assinados
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : itens.length === 0 ? (
          <p className="text-muted-foreground text-center py-6">
            Nenhum documento assinado neste atendimento.
          </p>
        ) : (
          <ul className="space-y-3">
            {itens.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3 border rounded-lg bg-muted/30"
              >
                <div>
                  <p className="font-medium">
                    {TIPO_LABEL[item.tipo_protocolo] ?? item.tipo_protocolo}{' '}
                    <span className="text-muted-foreground text-sm">
                      (v.{item.versao})
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatarData(item.data_assinatura)}
                    {item.assinante && ` · ${item.assinante}`}
                  </p>
                </div>
                <a
                  href={urlValidar(item.codigo_validacao)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Validar documento
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
